import { useRef } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import { EmptystatePrColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import PageWrapper from "components/PageWrapper";
import { HUMANIZE_PROVIDER } from "constants/vcsProviders";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableWrapper from "ds/components/Table/Wrapper";
import TableSimpleHead from "ds/components/TableSimple/Head";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useStackPullRequests from "shared/Stack/useStackPullRequests";
import { VcsProvider } from "types/generated";

import StackDetachedIntegrationCallout from "../../components/DetachedIntegrationCallout";
import StackHeader from "../../components/Header";
import { StackContext } from "../../Context";
import { getStacksBackUrl } from "../../helpers";
import StackPullRequestsTableRows from "./Table/Rows";
import useStackPullRequestsTableStore from "./Table/useTableStore";

const TABLE_ARIA_LABEL = "Stack pull requests";

const StackPullRequestsList = () => {
  const { stack: stackCtx } = useTypedContext(StackContext);
  const tableRef = useRef<HTMLDivElement>(null);

  const providerBasedName =
    stackCtx.provider === VcsProvider.Gitlab ? "Merge Request" : "Pull Request";
  const pageTitle = stackCtx.provider === VcsProvider.Gitlab ? "Merge requests" : "Pull requests";
  const emptyStateTitle =
    stackCtx.provider === VcsProvider.Gitlab ? "No merge requests yet" : "No pull requests yet";

  useTitle(`${pageTitle} · ${stackCtx.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stackCtx.name,
    },
  ]);

  const { pullRequests, error, isPageLoading, isRefreshing, isEmpty, isNotFound, refresh } =
    useStackPullRequests(stackCtx.id, { skip: stackCtx.vcsDetached });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  if (isNotFound) {
    return <NotFoundPage />;
  }

  return (
    <>
      <StackHeader />

      <PageInfoHeader title={pageTitle}>
        <Button onPress={refresh} loading={isRefreshing} variant="primary">
          Refresh
        </Button>
      </PageInfoHeader>

      <StackDetachedIntegrationCallout />

      <PageWrapper margin="0">
        {stackCtx.vcsDetached && (
          <EmptyState
            title="Source code integration was detached from this stack."
            icon={EmptystatePrColored}
          />
        )}

        {!stackCtx.vcsDetached && (
          <TableWrapper ref={tableRef} hasPagination={false}>
            {isEmpty && (
              <EmptyState
                title={emptyStateTitle}
                icon={EmptystatePrColored}
                caption={
                  <>
                    This is a list of {providerBasedName}s currently open against this stack's
                    tracked branch on {HUMANIZE_PROVIDER[stackCtx.provider]}. If something seems
                    missing, it may be because the {providerBasedName} was opened before the
                    creation of this stack, or it may be targeting a different branch.
                  </>
                }
              />
            )}

            {!isEmpty && (
              <Table ariaLabel={TABLE_ARIA_LABEL} role="treegrid">
                <TableSimpleHead
                  store={useStackPullRequestsTableStore}
                  ariaLabel={TABLE_ARIA_LABEL}
                  tableRef={tableRef}
                />
                <TableBody>
                  <StackPullRequestsTableRows pullRequests={pullRequests} tableRef={tableRef} />
                </TableBody>
              </Table>
            )}
          </TableWrapper>
        )}
      </PageWrapper>
    </>
  );
};

export default StackPullRequestsList;
